/**
 * Логика колоды и генерации карт с seeded RNG
 */

import { getElementsByLevel } from './elements';

/**
 * Seeded RNG (Mulberry32)
 * Генерирует детерминированную последовательность случайных чисел
 */
export class SeededRandom {
    private seed: number;

    constructor(seed: number) {
        this.seed = seed >>> 0; // Ensure 32-bit unsigned integer
    }

    /**
     * Возвращает случайное число от 0 до 1
     */
    next(): number {
        let t = this.seed += 0x6D2B79F5;
        t = Math.imul(t ^ t >>> 15, t | 1);
        t ^= t + Math.imul(t ^ t >>> 7, t | 61);
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    }

    /**
     * Возвращает случайное целое число от min до max (включительно)
     */
    nextInt(min: number, max: number): number {
        return Math.floor(this.next() * (max - min + 1)) + min;
    }

    /**
     * Выбирает случайный элемент из массива
     */
    choice<T>(arr: T[]): T {
        return arr[this.nextInt(0, arr.length - 1)];
    }
}

/**
 * Распределение карт для первых 10 ходов
 * Постепенное увеличение сложности
 */
const TURN_DISTRIBUTION: Record<number, number[]> = {
    1: [1],           // Ходы 1-2: уровни 1-2
    2: [1, 2],
    3: [1, 2],        // Ходы 3-4: уровни 1-3
    4: [1, 2],
    5: [1, 2],     // Ходы 5-6: уровни 1-4
    6: [1, 2, 3],
    7: [1, 2, 3],  // Ходы 7-8: уровни 1-5
    8: [1, 2, 3],
    9: [1, 2, 3, 4],  // Ходы 9-10: все уровни (кроме 6)
    10: [1, 2, 3, 4, 5],
};

/**
 * Генератор карт (on-demand)
 */
export class DeckGenerator {
    private rng: SeededRandom;

    constructor(seed: number) {
        this.rng = new SeededRandom(seed);
    }


    /**
     * Получить ключ элемента для заданного хода (БЕЗ создания карты)
     * @param turnNumber - номер хода (начинается с 1)
     */
    getElementForTurn(turnNumber: number): string {
        // Первые 10 ходов: используем фиксированное распределение
        if (turnNumber <= 10) {
            return this.getElementForEarlyGame(turnNumber);
        }

        // После 10 хода: 40% базовые, 60% случайные уровни 2-5
        const roll = this.rng.next();

        if (roll < 0.4) {
            // 40% - базовые элементы (уровень 1)
            return this.getBaseElement();
        } else {
            // 60% - случайные уровни 2-5
            const level = this.rng.choice([2, 3, 4, 5]);
            return this.getElementOfLevel(level);
        }
    }

    /**
     * Получить элемент для первых 10 ходов (БЕЗ создания карты)
     */
    private getElementForEarlyGame(turnNumber: number): string {
        const availableLevels = TURN_DISTRIBUTION[turnNumber] || [1, 2, 3, 4, 5];

        // 50% шанс базовых элементов в первых 10 ходах
        if (this.rng.next() < 0.5 && availableLevels.includes(1)) {
            return this.getBaseElement();
        }

        // Выбираем случайный доступный уровень
        const level = this.rng.choice(availableLevels);
        return this.getElementOfLevel(level);
    }

    /**
     * Получить базовый элемент (уровень 1) (БЕЗ создания карты)
     */
    private getBaseElement(): string {
        const baseElements = getElementsByLevel(1);
        const [elementKey] = this.rng.choice(baseElements);
        return elementKey;
    }

    /**
     * Получить элемент заданного уровня (БЕЗ создания карты)
     */
    private getElementOfLevel(level: number): string {
        const elements = getElementsByLevel(level);

        if (elements.length === 0) {
            // Fallback на базовые элементы
            return this.getBaseElement();
        }

        const [elementKey] = this.rng.choice(elements);
        return elementKey;
    }


    /**
     * Получить элементы для стартовых карт на столе (6 карт, только базовые)
     */
    generateTableElements(): string[] {
        const elements: string[] = [];
        for (let i = 0; i < 6; i++) {
            elements.push(this.getBaseElement());
        }
        return elements;
    }

    /**
     * Получить элементы для стартовых карт Игрока 1 (3 случайные базовые)
     */
    generatePlayer1Elements(): string[] {
        const elements: string[] = [];
        for (let i = 0; i < 3; i++) {
            elements.push(this.getBaseElement());
        }
        return elements;
    }

    /**
     * Получить элементы для стартовых карт Игрока 2 (все 4 базовые: fire, water, earth, air)
     */
    generatePlayer2Elements(): string[] {
        return ['fire', 'water', 'earth', 'air'];
    }

}